import { inject } from 'mobx-react';
import PropTypes from 'prop-types';
import React from 'react';
import Skeleton from 'react-loading-skeleton';
import { Container } from 'reactstrap';

import { modelOf } from '../../../prop-types';
import AdStore from '../../../store/AdStore';
import ConfigStore from '../../../store/ConfigStore';
import AdZones from '../../../types/AdZones';
import { blockTestRender } from '../../../util/process';
import PageTitle from '../../common/PageTitle';
import GenericLayout from '../../layout/GenericLayout';
import PageSkeleton from '../PageSkeleton';

const ProductListPageSkeletonVariants = Object.freeze({
  ON_SALE: 'onSale',
  NEW_PRODUCTS: 'newProducts',
  SOLD_PRODUCTS: 'soldProducts',
  POPULAR_PRODUCTS: 'popularProducts',
  RECOMMENDED_PRODUCTS: 'recommendedProducts',
  MANUFACTURER: 'manufacturer',
  WISHLIST: 'wishlist',
});

const containerClassByVariant = {
  [ProductListPageSkeletonVariants.ON_SALE]: 'OnSalePage__container',
  [ProductListPageSkeletonVariants.NEW_PRODUCTS]: 'NewProductsPage__container',
  [ProductListPageSkeletonVariants.SOLD_PRODUCTS]:
    'SoldProductsPage__container',
  [ProductListPageSkeletonVariants.POPULAR_PRODUCTS]:
    'PopularProductsPage__container',
  [ProductListPageSkeletonVariants.RECOMMENDED_PRODUCTS]:
    'RecommendedProductsPage__container',
  [ProductListPageSkeletonVariants.MANUFACTURER]: 'ManufacturerPage__container',
  [ProductListPageSkeletonVariants.WISHLIST]: 'WishListPage__container',
};

const ProductListPageSkeleton = ({
  adStore,
  configStore,
  variant,
  showBannerSkeleton,
}) => {
  const containerClassName = containerClassByVariant[variant];

  if (!containerClassName) {
    return null;
  }

  // ManufacturerPage reserves the main banner when the zone id is present —
  // match that contract (not viewport-sensitive shouldShowZone).
  const shouldReserveBanner =
    variant === ProductListPageSkeletonVariants.MANUFACTURER
      ? adStore.manufacturerPageAds.indexOf(AdZones.MAIN_BANNER) !== -1
      : showBannerSkeleton;

  return (
    <>
      {shouldReserveBanner && blockTestRender() && (
        <Skeleton height={configStore.banner.mainBanner.height} />
      )}
      <Container className={containerClassName}>
        <GenericLayout
          content={
            <div className="ProductListPageSkeleton">
              {blockTestRender() && <Skeleton height={20} width={260} />}
              <div className="Skeleton__spacer" />
              <PageTitle>
                {blockTestRender() && <Skeleton height={40} width={260} />}
              </PageTitle>
              <div className="Skeleton__spacer" />
              <PageSkeleton />
            </div>
          }
        />
      </Container>
    </>
  );
};

ProductListPageSkeleton.propTypes = {
  adStore: modelOf(AdStore).isRequired,
  configStore: modelOf(ConfigStore).isRequired,
  variant: PropTypes.oneOf(Object.values(ProductListPageSkeletonVariants))
    .isRequired,
  showBannerSkeleton: PropTypes.bool,
};

ProductListPageSkeleton.defaultProps = {
  showBannerSkeleton: false,
};

export default inject('adStore', 'configStore')(ProductListPageSkeleton);
